// import {} from "mapv-three";

import * as THREE from 'bmap-three';
import { arrify } from '../arrify';
import { isEmpty } from '../is';
import { initHeadMap3D, useHeatmapBlock } from './headmap';
import { useLineManager } from './line-manager';
import { useSimplePointManager } from './points-manager';
import { useGradient } from './useGradient';
import { UAVManager } from './uav-manager';
import { Pillar } from './pillar';

export * from './tube';
export * from './line-manager';
export * from './spark-manager';
export * from './uav-manager';
export * from './label-manager';
export * from './sky';
export * from './loader';

export class GeoModel {
  constructor({ engine, type, prop, gradient, max, height, devicePath }) {
    this.gradient = gradient || {
      0: 'rgb(50, 250, 56)',
      0.5: 'rgb(250, 250, 56)',
      1: 'rgb(250, 50, 56)'
    };

    this.gradientUtil = useGradient(this.gradient);

    this.devicePath = devicePath;
    this.defaultMax = 999999;
    this.max = max;
    this.type = type || 'line';
    this.prop = prop;
    this.engine = engine;
    this.height = height || 10;

    /**
     * @type { heatmap3D, heatmapBlock, point }
     */
    this.deviceMap = {};
    this.tasks = [];

    this.dict = ['heatmap3D', 'heatmapBlock', 'point', 'pillar'];
  }

  async init() {
    await this.initLine();
    await this.initUAV();

    return this;
  }

  setMax(max, isRefresh = true) {
    this.max = max;
    Object.values(this.deviceMap).forEach(device => {
      this.dict.forEach(key => {
        device[key].setMax?.(max[this.prop] || this.defaultMax);
      });
    });

    if (isRefresh) {
      Object.values(this.uav.moveConfig).forEach(e => {
        this.refresh(e);
      });
    }
  }

  async initHeatMapBlock() {
    return await useHeatmapBlock({
      config: {
        opacity: 1,
        size: 10, // 热力绘制半径
        textColor: '#000', // 文本颜色
        radiationSize: 0, // 效果半径
        maxValue: 10, // 最大热力值
        resolution: 0,
        attenuateMValueFactor: 1, //径向渐变速度
        gradient: this.gradient,
        maxLerpLen: 1000
      },
      engine: this.engine
    });
  }

  initPillar() {
    return new Pillar({
      engine: this.engine,
      size: 10,
      polygonOpts: {
        extrude: true,
        vertexColors: true,
        vertexHeights: true,
        opacity: 0.8
      }
    });
  }

  async initHeatMap3D() {
    return await initHeadMap3D({
      config: {
        opacity: 1,
        radius: 5, // 热力绘制半径
        maxValue: 10, // 最大热力值
        heightRatio: 10, // 高度系数
        attenuateMValueFactor: 1, //径向渐变速度
        gradient: this.gradient
      },
      engine: this.engine
    });
  }

  async initPoints() {
    return await useSimplePointManager({
      engine: this.engine,
      size: 2
    });
  }

  async initLine() {
    this.line = await useLineManager({
      engine: this.engine
    });

    return this;
  }

  async initUAV() {
    this.uav = new UAVManager({
      url: this.devicePath,
      offset: [0, 0, 0],
      engine: this.engine
    });

    await this.uav.init();

    this.uav.onMove(item => {
      this.tasks.push(() => {
        this.refresh(item);
      });
    });

    return this;
  }

  getHeight(v, max) {
    return (v / max) * this.height;
  }

  refresh(item) {
    const has = this.makeTypeValidator();

    const lineData = [];

    let tempColor, tempHeight;

    const data = item.data
      .slice(0, item.ind + 1)
      .map(e => {
        if (isEmpty(e) || isEmpty(e[this.prop])) {
          if (!has('line') && !has('heatmapBlock') && !has('pillar')) return;

          lineData.push({
            position: e.position,
            [this.prop]: e[this.prop] || 0,
            color: this.gradientUtil.getColor(0, 'hex'),
            height: 1
          });

          return;
        }
        let sensorValue = e[this.prop].match(/^-?\d+(\.\d+)?/)[0];

        tempColor = this.gradientUtil.getColor(
          sensorValue / (this.max[this.prop] || this.defaultMax),
          'hex'
        );

        tempHeight = this.getHeight(sensorValue, this.max[this.prop] || this.defaultMax);

        const item = {
          [this.prop]: e[this.prop],
          position: e.position,
          color: tempColor,
          height: tempHeight
        };

        lineData.push(item);

        return item;
      })
      .filter(Boolean);

    if (has('line')) {
      this.line.setAggregationLine({
        name: item.name,
        flyData: lineData,
        basicData: lineData,
        flyReset: true,
        basicReset: true
      });
    }

    const device = this.deviceMap[item.name];

    if (isEmpty(device)) return;

    this.setMax(this.max, false);

    has('point') && device.point.setData(data);
    has('heatmap3D') && device.heatmap3D.setData(data);
    has('heatmapBlock') && device.heatmapBlock.setData(lineData);
    has('pillar') && device.pillar.setData(lineData);

    return this;
  }

  async initDeviceParts(name, pos) {
    this.deviceMap[name] = {
      point: await this.initPoints(),
      heatmap3D: await this.initHeatMap3D(),
      heatmapBlock: await this.initHeatMapBlock(),
      pillar: this.initPillar(),
      data: []
    };

    this.uav.addUAV({
      name: name,
      position: pos,
      onClick: e => {
        this.engine.map.setCenter(e.point);
      }
    });

    this.line.addAggregationLine({
      name: name,
      flyConfig: {
        vertexColors: false,
        lineWidth: 2.5
      },
      basicConfig: {
        lineWidth: 2,
        vertexColors: true
      },
      basicData: [],
      flyData: []
    });

    this.showGraph(this.type);
    this.setProp(this.prop, false);

    return this.deviceMap[name];
  }

  /**
   * 设置数据
   * @param {string} name
   * @param {Record<string, number>[] | Record<string, number>}} data
   */
  async setData(name, data) {
    let device = this.deviceMap[name];

    data = arrify(data);

    if (!device) {
      device = await this.initDeviceParts(name, data.slice(-1)[0].position);
    }

    data.forEach(e => {
      device.data.push(e);

      const { position, ...rest } = e;
      if (position[0] == 0 && position[1] == 0) return;

      this.uav.moveTo({
        name: name,
        step: 2,
        data: {
          position: position,
          ...rest
        },
        position: position
      });
    });

    return this;
  }

  async resetData(name, data) {
    let device = this.deviceMap[name];

    data = arrify(data);

    if (isEmpty(data)) {
      if (device) {
        this.remove(name);
      }
    } else {
      if (!device) {
        device = await this.initDeviceParts(name, data.slice(-1)[0].position);
      }

      device.data = data;

      this.uav.resetData({ name, data });
    }

    return this;
  }

  remove(name) {
    let device = this.deviceMap[name];

    if (!device) return;

    this.uav.delUAV(name);

    this.line.delAggregationLine(name);

    device.heatmap3D.dispose();
    device.heatmapBlock.dispose();
    device.point.dispose();

    delete this.deviceMap[name];
  }

  setMoveSpeed(name, step) {
    this.uav.moveConfig[name].step = step;
  }

  async insertBefore(name, data) {
    let device = this.deviceMap[name];

    data = arrify(data);

    if (!device) {
      device = await this.initDeviceParts(name, data.slice(-1)[0].position);
      device.data.push(...data);
    }

    this.uav.insertBefore(name, data);

    this.refresh(this.uav.moveConfig[name]);

    return this;
  }

  enableMove(flag) {
    this.uav?.enableMove(flag);

    return this;
  }

  render() {
    var clock = new THREE.Clock();

    this.engine.rendering.addBeforeRenderListener(() => {
      if (!isEmpty(this.tasks)) {
        this.tasks.pop()();
      }

      this.uav?.update(clock.getDelta() * 30);
    });

    return this;
  }

  makeTypeValidator() {
    const types = this.type.split(',');

    return (...rest) => {
      const res = rest.filter(e => types.includes(e));
      return res.length === rest.length;
    };
  }

  /**
   *
   * @param {'heatmap3D' | 'heatmapBlock' | 'line' | 'point'} type
   */
  showGraph(type, isRefresh = true) {
    this.type = type;

    const has = this.makeTypeValidator();

    this.line.setVisible(has('line'));

    Object.values(this.deviceMap).forEach(device => {
      this.dict.forEach(key => {
        device[key].setVisible(has(key));
      });
    });

    if (isRefresh) {
      Object.values(this.uav.moveConfig).forEach(e => {
        this.refresh(e);
      });
    }

    return this;
  }

  setProp(prop, isRefresh = true) {
    this.prop = prop;

    Object.values(this.deviceMap).forEach(device => {
      this.dict.forEach(key => {
        device[key].useProp?.(prop);
      });
    });

    if (isRefresh) {
      Object.values(this.uav.moveConfig).forEach(e => {
        this.refresh(e);
      });
    }

    return this;
  }

  setGradient(gradient, isRefresh = true) {
    this.gradient = gradient;

    this.gradientUtil = useGradient(this.gradient);

    Object.values(this.deviceMap).forEach(device => {
      this.dict.forEach(key => {
        device[key].setGradient?.(this.gradient);
      });
    });

    if (isRefresh) {
      Object.values(this.uav.moveConfig).forEach(e => {
        this.refresh(e);
      });
    }
  }
}

export function makePointGeoJson(data, opt) {
  opt = opt || { position: [1, 1, 1] };

  return arrify(data).map(e => {
    const { position, ...properties } = e;

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [
          position[0] * opt.position[0],
          position[1] * opt.position[1],
          position[2] * opt.position[2]
        ]
      },
      properties
    };
  });
}

export function makeLineStringGeoJson(data, opt) {
  if (data.length === 0) return [];

  opt = opt || { position: [1, 1, 1] };

  const __data = [data[0]].concat(data);

  const color = __data.map(e => e.color);
  const position = __data.map(e => e.position);

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {
          color: color
        },
        geometry: {
          type: 'LineString',
          coordinates: position.map(p => [
            p[0] * opt.position[0],
            p[1] * opt.position[1],
            p[2] * opt.position[2]
          ])
        }
      }
    ]
  };
}

export function randomPoints(center, radius, amount) {
  var features = [];
  for (var i = 0; i < amount; i++) {
    features.push({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [
          center[0] + radius * (Math.random() - 0.5) * 2,
          center[1] + radius * (Math.random() - 0.5) * 2,
          (center[2] || 0) + radius * Math.random()
        ]
      },
      properties: { count: 10 * Math.random() }
    });
  }
  return features;
}
